<template>
  <v-dialog
    data-testid="history-dialog"
    scrollable
    width="484px"
    :value="show"
    :title="$t('history.history')"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card :loading="isLoading">
      <v-card-title class="px-6">
        {{ $t('history.history') }}
      </v-card-title>

      <v-card-text class="px-7">
        <v-list class="ma-0 pa-0 mb-5">
          <HistoryLogItem
            v-for="logItem of items"
            :key="logItem._id"
            :logItem="logItem"
          />
        </v-list>
      </v-card-text>

      <v-pagination
        v-if="totalPages > 1"
        circle
        class="my-4"
        total-visible="5"
        data-testid="history-dialog-pagination"
        :length="totalPages"
        :value="currentPage"
        @input="handlePagination"
      ></v-pagination>

      <v-divider />

      <v-card-actions class="align-center justify-end px-6 py-4">
        <Button
          data-testid="history-button-close"
          color="blue"
          :text="$t('common.button.close')"
          @click="$emit('close')"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'

import Button from '@/components/UI/Button'
import HistoryLogItem from '@/components/HistoryLogItem.vue'

import mixin from '@/mixins/mixin.vue'

const ITEMS_PER_PAGE = 8

export default {
  name: 'HistoryModal',

  components: { Button, HistoryLogItem },

  mixins: [mixin],

  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    routineId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      items: [],
      totalItems: 0,
      currentPage: 1,
      totalPages: 0,
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters(['organization']),

    entityName() {
      const customName = this.organization?.customNamings?.[this.revType]
        ?.singular

      return (
        customName ||
        this.$t(`common.rev_type.${_.snakeCase(this.revType)}_one`)
      )
    },
  },

  mounted() {
    return this.fetchLogs()
  },

  methods: {
    close() {
      this.$emit('close')
    },

    async fetchLogs(pageNumber = 1) {
      try {
        this.isLoading = true

        const { data } = await api.audit.getRoutineLogs({
          organizationId: this.organization._id,
          routineId: this.routineId,
          page: pageNumber,
          limit: ITEMS_PER_PAGE,
          sort: {
            by: 'eventTime',
            order: 'desc',
          },
        })

        const { results, total, page, pages } = data

        this.items = results
        this.totalItems = total
        this.totalPages = pages
        this.currentPage = page
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoading = false
      }
    },

    handlePagination(nextPage) {
      this.fetchLogs(nextPage)
    },

    getLogMessage(log) {
      if (log.eventName.includes('updated'))
        return this.$t('history.update_event', { entityName: this.entityName })
      if (log.eventName.includes('created'))
        return this.$t('history.create_event', { entityName: this.entityName })
      return ''
    },

    getIcon(log) {
      if (log.eventName.includes('updated')) return 'mdi-cog-clockwise'
      if (log.eventName.includes('created')) return 'mdi-plus-circle'
      return ''
    },

    getIconColor(log) {
      if (log.eventName.includes('updated')) return 'blue-grey darken-1'
      if (log.eventName.includes('created')) return 'green'
      return ''
    },
  },
}
</script>
